import React, { useState, useEffect } from 'react'
import { InlineEdit } from '@/components/shared/InlineEdit'
import { formatCurrency } from '@/lib/constants'
import { supabase } from '@/lib/supabase'

// ─── Data Model ─────────────────────────────────────────────────────────────────

interface ReturnRateEntry {
  rate: number
  startYear: number
  endYear?: number
}

interface RangedYearlyEntry {
  amount: number
  startYear: number
  endYear?: number
}

interface RangedMonthlyEntry {
  amount: number
  startYear: number
  startMonth?: number
  endYear?: number
  endMonth?: number
}

interface OneOffEntry {
  date: string
  amount: number
}

interface AccountAdditions {
  yearly: RangedYearlyEntry[]
  monthly: RangedMonthlyEntry[]
  manual: OneOffEntry[]
  withdrawals: {
    yearly: RangedYearlyEntry[]
    monthly: RangedMonthlyEntry[]
    oneOff: OneOffEntry[]
  }
}

interface AccountProjection {
  name: string
  returnRates: ReturnRateEntry[]
  taxEnabled: boolean
  linkedAccounts: string[]
  years: { year: number; starting: number; projected: number; actual?: number; tax?: number }[]
  startingOverrides?: Set<number>
  additions?: AccountAdditions
}

interface AdditionalInvestment {
  year: number
  amount: number
}

interface SavedProjection {
  id: string
  name: string
  initialInvestment: number
  growthRate: number
  numYears: number
  additionalInvestments: AdditionalInvestment[]
  table: { year: number; starting: number; growth: number; additional: number; ending: number }[]
}

// ─── Constants ──────────────────────────────────────────────────────────────────

const PROJECTION_END_YEAR = 2045
const HIGHLIGHT_YEARS = [2042, 2043, 2044, 2045]
const STORAGE_KEY = 'tradingApp_projections'
const ALL_ACCOUNT_SLUGS = ['wheel', 'amzn', 'leap-calls', 'leap-puts', 'richas-roth', 'yogeshs-roth', 'hsa', 'spy-spread', 'shares']
const TAX_RATE = 0.35

// ─── Calculation Engine ─────────────────────────────────────────────────────────

function getEffectiveReturn(returnRates: ReturnRateEntry[], year: number): number {
  const matching = returnRates.filter(
    e => e.startYear <= year && (e.endYear === undefined || e.endYear >= year)
  )
  if (matching.length === 0) return 0
  return matching.reduce((best, e) => (e.startYear > best.startYear ? e : best)).rate
}

function getYearlyTotal(entries: RangedYearlyEntry[], year: number): number {
  return entries
    .filter(e => e.startYear <= year && (e.endYear === undefined || e.endYear >= year))
    .reduce((sum, e) => sum + e.amount, 0)
}

function getMonthlyDetails(entries: RangedMonthlyEntry[], year: number): { activeMonths: number[]; totalForYear: number } {
  const activeMonths: Set<number> = new Set()
  let total = 0
  for (const entry of entries) {
    const sm = entry.startMonth ?? 1
    const em = entry.endMonth ?? 12
    const ey = entry.endYear ?? entry.startYear
    if (year < entry.startYear || year > ey) continue
    const monthStart = year === entry.startYear ? sm : 1
    const monthEnd = year === ey ? em : 12
    for (let m = monthStart; m <= monthEnd; m++) {
      activeMonths.add(m)
      total += entry.amount
    }
  }
  return { activeMonths: Array.from(activeMonths).sort((a, b) => a - b), totalForYear: total }
}

function getOneOffsForYear(entries: OneOffEntry[], year: number): OneOffEntry[] {
  return entries.filter(e => e.date.startsWith(String(year)))
}

function computeProRatedProjection(
  starting: number,
  rate: number,
  yearlyAdd: number,
  monthlyEntries: { amount: number; months: number[] }[],
  oneOffEntries: OneOffEntry[],
  yearlyWithd: number,
  monthlyWithdEntries: { amount: number; months: number[] }[],
  oneOffWithdEntries: OneOffEntry[]
): number {
  const r = rate / 100
  // Starting gets full rate
  let result = starting * (1 + r)
  // Yearly additions get full rate
  result += yearlyAdd * (1 + r)
  // Monthly additions: each active month gets (13 - month) / 12 of rate growth + the amount
  for (const entry of monthlyEntries) {
    for (const m of entry.months) {
      result += entry.amount * (1 + ((13 - m) / 12) * r)
    }
  }
  // One-off additions
  for (const entry of oneOffEntries) {
    const month = parseInt(entry.date.slice(5, 7), 10) || 6
    result += entry.amount * (1 + ((12 - (month - 1)) / 12) * r)
  }
  // Yearly withdrawals get full rate (subtracted)
  result -= yearlyWithd * (1 + r)
  // Monthly withdrawals
  for (const entry of monthlyWithdEntries) {
    for (const m of entry.months) {
      result -= entry.amount * (1 + ((13 - m) / 12) * r)
    }
  }
  // One-off withdrawals
  for (const entry of oneOffWithdEntries) {
    const month = parseInt(entry.date.slice(5, 7), 10) || 6
    result -= entry.amount * (1 + ((12 - (month - 1)) / 12) * r)
  }
  return Math.round(result)
}

function computeTax(starting: number, netAdditions: number, endingValue: number, taxEnabled: boolean): number {
  if (!taxEnabled) return 0
  const gains = endingValue - starting - netAdditions
  return Math.max(0, Math.round(gains * TAX_RATE))
}

function getTotalAdditionsForYear(additions: AccountAdditions | undefined, year: number): number {
  if (!additions) return 0
  const yearly = getYearlyTotal(additions.yearly, year)
  const { totalForYear: monthly } = getMonthlyDetails(additions.monthly, year)
  const oneOffs = getOneOffsForYear(additions.manual, year)
  const oneOffTotal = oneOffs.reduce((s, e) => s + e.amount, 0)
  return yearly + monthly + oneOffTotal
}

function getTotalWithdrawalsForYear(additions: AccountAdditions | undefined, year: number): number {
  if (!additions) return 0
  const yearly = getYearlyTotal(additions.withdrawals.yearly, year)
  const { totalForYear: monthly } = getMonthlyDetails(additions.withdrawals.monthly, year)
  const oneOffs = getOneOffsForYear(additions.withdrawals.oneOff, year)
  const oneOffTotal = oneOffs.reduce((s, e) => s + e.amount, 0)
  return yearly + monthly + oneOffTotal
}

function getNetAdditionsForYear(additions: AccountAdditions | undefined, year: number): number {
  return getTotalAdditionsForYear(additions, year) - getTotalWithdrawalsForYear(additions, year)
}

// Recalculate all years for an account
function recalculateAllYears(account: AccountProjection): AccountProjection {
  const years = [...account.years.map(y => ({ ...y }))]
  const overrides = new Set(account.startingOverrides || [])

  for (let i = 0; i < years.length; i++) {
    const year = years[i].year
    const rate = getEffectiveReturn(account.returnRates, year)

    // Determine starting
    if (i > 0 && !overrides.has(i)) {
      const prev = years[i - 1]
      const prevEnding = (prev.actual !== undefined && prev.actual !== null && prev.actual > 0)
        ? prev.actual
        : prev.projected
      const prevTax = prev.tax ?? 0
      years[i].starting = Math.round(prevEnding - prevTax)
    }

    // Compute projected with pro-rating
    const addYearly = getYearlyTotal(account.additions?.yearly || [], year)
    const addMonthlyDetails = getMonthlyDetails(account.additions?.monthly || [], year)
    const addOneOffs = getOneOffsForYear(account.additions?.manual || [], year)
    const wdYearly = getYearlyTotal(account.additions?.withdrawals?.yearly || [], year)
    const wdMonthlyDetails = getMonthlyDetails(account.additions?.withdrawals?.monthly || [], year)
    const wdOneOffs = getOneOffsForYear(account.additions?.withdrawals?.oneOff || [], year)

    // Build monthly entries for pro-rating
    const monthlyAddEntries: { amount: number; months: number[] }[] = []
    for (const entry of (account.additions?.monthly || [])) {
      const sm = entry.startMonth ?? 1
      const em = entry.endMonth ?? 12
      const ey = entry.endYear ?? entry.startYear
      if (year < entry.startYear || year > ey) continue
      const monthStart = year === entry.startYear ? sm : 1
      const monthEnd = year === ey ? em : 12
      const months: number[] = []
      for (let m = monthStart; m <= monthEnd; m++) months.push(m)
      if (months.length > 0) monthlyAddEntries.push({ amount: entry.amount, months })
    }

    const monthlyWdEntries: { amount: number; months: number[] }[] = []
    for (const entry of (account.additions?.withdrawals?.monthly || [])) {
      const sm = entry.startMonth ?? 1
      const em = entry.endMonth ?? 12
      const ey = entry.endYear ?? entry.startYear
      if (year < entry.startYear || year > ey) continue
      const monthStart = year === entry.startYear ? sm : 1
      const monthEnd = year === ey ? em : 12
      const months: number[] = []
      for (let m = monthStart; m <= monthEnd; m++) months.push(m)
      if (months.length > 0) monthlyWdEntries.push({ amount: entry.amount, months })
    }

    years[i].projected = computeProRatedProjection(
      years[i].starting, rate, addYearly, monthlyAddEntries, addOneOffs,
      wdYearly, monthlyWdEntries, wdOneOffs
    )

    // Compute tax
    const endingValue = (years[i].actual !== undefined && years[i].actual !== null && years[i].actual! > 0)
      ? years[i].actual!
      : years[i].projected
    const netAdd = getNetAdditionsForYear(account.additions, year)
    years[i].tax = computeTax(years[i].starting, netAdd, endingValue, account.taxEnabled)
  }

  return { ...account, years }
}

// ─── Migration Helper ───────────────────────────────────────────────────────────

function migrateOldProjection(saved: Record<string, unknown>): Partial<AccountProjection> {
  const result: Partial<AccountProjection> = {}

  // Migrate target_return → returnRates
  if (saved.return_rates && Array.isArray(saved.return_rates) && saved.return_rates.length > 0) {
    result.returnRates = saved.return_rates as ReturnRateEntry[]
  } else if (saved.target_return !== undefined) {
    result.returnRates = [{ rate: Number(saved.target_return), startYear: 2026 }]
  }

  // Migrate tax_enabled
  if (saved.tax_enabled !== undefined && saved.tax_enabled !== null) {
    result.taxEnabled = Boolean(saved.tax_enabled)
  }

  // Migrate linked_accounts
  if (saved.linked_accounts && Array.isArray(saved.linked_accounts)) {
    result.linkedAccounts = saved.linked_accounts as string[]
  } else {
    result.linkedAccounts = []
  }

  // Migrate additions
  if (saved.additions) {
    const old = saved.additions as Record<string, unknown>
    const newAdditions: AccountAdditions = {
      yearly: [],
      monthly: [],
      manual: [],
      withdrawals: { yearly: [], monthly: [], oneOff: [] },
    }

    // Migrate yearly additions
    if (old.yearly) {
      if (Array.isArray(old.yearly)) {
        newAdditions.yearly = old.yearly as RangedYearlyEntry[]
      } else if (typeof old.yearly === 'object' && old.yearly !== null) {
        for (const [yr, amt] of Object.entries(old.yearly as Record<string, number>)) {
          newAdditions.yearly.push({ amount: Number(amt), startYear: Number(yr) })
        }
      }
    }

    // Migrate monthly additions
    if (old.monthly) {
      if (Array.isArray(old.monthly)) {
        newAdditions.monthly = old.monthly as RangedMonthlyEntry[]
      } else if (typeof old.monthly === 'object' && old.monthly !== null) {
        for (const [yr, amt] of Object.entries(old.monthly as Record<string, number>)) {
          newAdditions.monthly.push({ amount: Number(amt), startYear: Number(yr) })
        }
      }
    }

    // Migrate manual (one-off) additions
    if (old.manual) {
      if (Array.isArray(old.manual)) {
        newAdditions.manual = old.manual as OneOffEntry[]
      } else if (typeof old.manual === 'object' && old.manual !== null) {
        for (const [, entries] of Object.entries(old.manual as Record<string, Array<{ date: string; amount: number } | number>>)) {
          for (const entry of entries) {
            if (typeof entry === 'number') {
              newAdditions.manual.push({ date: '2026-01-01', amount: entry })
            } else {
              newAdditions.manual.push({ date: entry.date, amount: entry.amount })
            }
          }
        }
      }
    }

    // Migrate withdrawals
    if (old.withdrawals && typeof old.withdrawals === 'object') {
      const w = old.withdrawals as Record<string, unknown>
      if (w.yearly) {
        if (Array.isArray(w.yearly)) {
          newAdditions.withdrawals.yearly = w.yearly as RangedYearlyEntry[]
        } else if (typeof w.yearly === 'object' && w.yearly !== null) {
          for (const [yr, amt] of Object.entries(w.yearly as Record<string, number>)) {
            newAdditions.withdrawals.yearly.push({ amount: Number(amt), startYear: Number(yr) })
          }
        }
      }
      if (w.monthly) {
        if (Array.isArray(w.monthly)) {
          newAdditions.withdrawals.monthly = w.monthly as RangedMonthlyEntry[]
        } else if (typeof w.monthly === 'object' && w.monthly !== null) {
          for (const [yr, amt] of Object.entries(w.monthly as Record<string, number>)) {
            newAdditions.withdrawals.monthly.push({ amount: Number(amt), startYear: Number(yr) })
          }
        }
      }
      if (w.oneOff) {
        if (Array.isArray(w.oneOff)) {
          newAdditions.withdrawals.oneOff = w.oneOff as OneOffEntry[]
        } else if (typeof w.oneOff === 'object' && w.oneOff !== null) {
          for (const [, entries] of Object.entries(w.oneOff as Record<string, Array<{ date: string; amount: number }>>)) {
            for (const entry of entries) {
              newAdditions.withdrawals.oneOff.push({ date: entry.date, amount: entry.amount })
            }
          }
        }
      }
    }

    result.additions = newAdditions
  }

  return result
}

// ─── Initial Projections ────────────────────────────────────────────────────────

function generateInitialYears(
  startYear: number,
  startingAmount: number,
  returnRates: ReturnRateEntry[],
  seedYears?: { year: number; starting: number; projected: number; actual?: number }[]
): { year: number; starting: number; projected: number; actual?: number; tax?: number }[] {
  const years: { year: number; starting: number; projected: number; actual?: number; tax?: number }[] = []
  let currentStarting = startingAmount
  for (let year = startYear; year <= PROJECTION_END_YEAR; year++) {
    const seed = seedYears?.find(s => s.year === year)
    if (seed) {
      years.push({ ...seed })
      currentStarting = seed.projected
    } else {
      const rate = getEffectiveReturn(returnRates, year)
      const projected = Math.round(currentStarting * (1 + rate / 100))
      years.push({ year, starting: Math.round(currentStarting), projected })
      currentStarting = projected
    }
  }
  return years
}

const defaultReturnRates: Record<string, ReturnRateEntry[]> = {
  'Options (Wheel)': [{ rate: 20, startYear: 2026 }],
  'HSA': [{ rate: 20, startYear: 2026 }],
  "Yogesh's Roth": [{ rate: 15, startYear: 2026 }],
  "Richa's Roth": [{ rate: 20, startYear: 2026 }],
  'Amazon': [{ rate: 10, startYear: 2026 }],
}

const defaultTaxEnabled: Record<string, boolean> = {
  'Options (Wheel)': true,
  'HSA': false,
  "Yogesh's Roth": false,
  "Richa's Roth": false,
  'Amazon': false,
}

const initialProjections: AccountProjection[] = [
  {
    name: 'Options (Wheel)', returnRates: [{ rate: 20, startYear: 2026 }], taxEnabled: true, linkedAccounts: [],
    years: generateInitialYears(2026, 70570, [{ rate: 20, startYear: 2026 }], [
      { year: 2026, starting: 70570, projected: 84684 },
      { year: 2027, starting: 84684, projected: 101621 },
      { year: 2028, starting: 101621, projected: 121945 },
      { year: 2029, starting: 121945, projected: 146334 },
      { year: 2030, starting: 146334, projected: 175601 },
    ]),
  },
  {
    name: 'HSA', returnRates: [{ rate: 20, startYear: 2026 }], taxEnabled: false, linkedAccounts: [],
    years: generateInitialYears(2026, 18633, [{ rate: 20, startYear: 2026 }], [
      { year: 2026, starting: 18633, projected: 22360 },
      { year: 2027, starting: 31110, projected: 37332 },
      { year: 2028, starting: 46082, projected: 55298 },
      { year: 2029, starting: 64048, projected: 76857 },
      { year: 2030, starting: 85607, projected: 102729 },
    ]),
  },
  {
    name: "Yogesh's Roth", returnRates: [{ rate: 15, startYear: 2026 }], taxEnabled: false, linkedAccounts: [],
    years: generateInitialYears(2026, 33776, [{ rate: 15, startYear: 2026 }], [
      { year: 2026, starting: 33776, projected: 38842 },
      { year: 2027, starting: 46342, projected: 53294 },
      { year: 2028, starting: 60794, projected: 69913 },
      { year: 2029, starting: 77413, projected: 89025 },
      { year: 2030, starting: 96525, projected: 111003 },
    ]),
  },
  {
    name: "Richa's Roth", returnRates: [{ rate: 20, startYear: 2026 }], taxEnabled: false, linkedAccounts: [],
    years: generateInitialYears(2026, 33048, [{ rate: 20, startYear: 2026 }], [
      { year: 2026, starting: 33048, projected: 39658 },
      { year: 2027, starting: 47158, projected: 56589 },
      { year: 2028, starting: 64089, projected: 76907 },
      { year: 2029, starting: 84407, projected: 101288 },
      { year: 2030, starting: 108788, projected: 130546 },
    ]),
  },
  {
    name: 'Amazon', returnRates: [{ rate: 10, startYear: 2026 }], taxEnabled: false, linkedAccounts: [],
    years: generateInitialYears(2026, 312480, [{ rate: 10, startYear: 2026 }], [
      { year: 2026, starting: 312480, projected: 343728 },
      { year: 2027, starting: 493728, projected: 543101 },
      { year: 2028, starting: 693101, projected: 762411 },
      { year: 2029, starting: 912411, projected: 1003652 },
      { year: 2030, starting: 1078652, projected: 1186517 },
    ]),
  },
]

// ─── Saved Custom Projections Helpers ────────────────────────────────────────────

function loadSavedProjections(): SavedProjection[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    return raw ? JSON.parse(raw) : []
  } catch {
    return []
  }
}

function saveSavedProjections(projections: SavedProjection[]) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(projections))
}

function computeProjectionTable(
  initialInvestment: number,
  growthRate: number,
  numYears: number,
  additionalInvestments: AdditionalInvestment[]
): { year: number; starting: number; growth: number; additional: number; ending: number }[] {
  const table: { year: number; starting: number; growth: number; additional: number; ending: number }[] = []
  const currentYear = new Date().getFullYear()
  let starting = initialInvestment

  for (let i = 0; i < numYears; i++) {
    const year = currentYear + i + 1
    const additional = additionalInvestments
      .filter(a => a.year === i + 1)
      .reduce((sum, a) => sum + a.amount, 0)
    const base = starting + additional
    const growth = base * (growthRate / 100)
    const ending = base + growth
    table.push({ year, starting, growth, additional, ending })
    starting = ending
  }

  return table
}

// ─── Config Panel Sub-Components ─────────────────────────────────────────────────

function RateEntryPill({ entry, onRemove }: { entry: ReturnRateEntry; onRemove: () => void }) {
  const label = entry.endYear ? `${entry.startYear}–${entry.endYear}: ${entry.rate}%` : `${entry.startYear}+: ${entry.rate}%`
  return (
    <span className="inline-flex items-center gap-1 px-2 py-1 bg-purple-100 border border-purple-300 rounded text-xs text-purple-800">
      {label}
      <button onClick={onRemove} className="text-purple-500 hover:text-purple-700">✕</button>
    </span>
  )
}

function AddRateEntryForm({ onAdd }: { onAdd: (entry: ReturnRateEntry) => void }) {
  const [rate, setRate] = useState('')
  const [startYear, setStartYear] = useState(2026)
  const [endYear, setEndYear] = useState('')
  return (
    <span className="inline-flex items-center gap-1 flex-wrap">
      <input type="number" value={rate} onChange={e => setRate(e.target.value)} placeholder="%" className="w-12 px-1 py-0.5 text-xs border border-[var(--color-border)] rounded" />
      <select value={startYear} onChange={e => setStartYear(Number(e.target.value))} className="px-1 py-0.5 text-xs border border-[var(--color-border)] rounded">
        {Array.from({ length: PROJECTION_END_YEAR - 2026 + 1 }, (_, i) => 2026 + i).map(y => <option key={y} value={y}>{y}</option>)}
      </select>
      <input type="number" value={endYear} onChange={e => setEndYear(e.target.value)} placeholder="End (opt)" className="w-16 px-1 py-0.5 text-xs border border-[var(--color-border)] rounded" />
      <button onClick={() => { if (Number(rate) > 0) { onAdd({ rate: Number(rate), startYear, endYear: endYear ? Number(endYear) : undefined }); setRate(''); setEndYear('') } }} className="px-1.5 py-0.5 text-xs text-white rounded bg-purple-500 hover:bg-purple-600">+</button>
    </span>
  )
}

function RangedYearlyPill({ entry, onRemove, color }: { entry: RangedYearlyEntry; onRemove: () => void; color: 'blue' | 'red' }) {
  const label = entry.endYear ? `${entry.startYear}–${entry.endYear}: $${entry.amount.toLocaleString()}/yr` : `${entry.startYear}+: $${entry.amount.toLocaleString()}/yr`
  const bgClass = color === 'red' ? 'bg-red-100 border-red-300 text-red-800' : 'bg-blue-100 border-blue-300 text-blue-800'
  const btnClass = color === 'red' ? 'text-red-500 hover:text-red-700' : 'text-blue-500 hover:text-blue-700'
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-1 border rounded text-xs ${bgClass}`}>
      {label}
      <button onClick={onRemove} className={btnClass}>✕</button>
    </span>
  )
}

function RangedMonthlyPill({ entry, onRemove, color }: { entry: RangedMonthlyEntry; onRemove: () => void; color: 'blue' | 'red' }) {
  const sm = entry.startMonth ?? 1
  const em = entry.endMonth ?? 12
  const ey = entry.endYear ?? entry.startYear
  const monthNames = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']
  const startLabel = `${monthNames[sm - 1]} ${entry.startYear}`
  const endLabel = entry.endYear ? `${monthNames[em - 1]} ${ey}` : `Dec ${entry.startYear}`
  const label = `${startLabel}–${endLabel}: $${entry.amount.toLocaleString()}/mo`
  const bgClass = color === 'red' ? 'bg-red-100 border-red-300 text-red-800' : 'bg-blue-100 border-blue-300 text-blue-800'
  const btnClass = color === 'red' ? 'text-red-500 hover:text-red-700' : 'text-blue-500 hover:text-blue-700'
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-1 border rounded text-xs ${bgClass}`}>
      {label}
      <button onClick={onRemove} className={btnClass}>✕</button>
    </span>
  )
}

function OneOffPill({ entry, onRemove, color }: { entry: OneOffEntry; onRemove: () => void; color: 'blue' | 'red' }) {
  const bgClass = color === 'red' ? 'bg-red-100 border-red-300 text-red-800' : 'bg-blue-100 border-blue-300 text-blue-800'
  const btnClass = color === 'red' ? 'text-red-500 hover:text-red-700' : 'text-blue-500 hover:text-blue-700'
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-1 border rounded text-xs ${bgClass}`}>
      {entry.date}: {formatCurrency(entry.amount)}
      <button onClick={onRemove} className={btnClass}>✕</button>
    </span>
  )
}

function AddRangedYearlyForm({ onAdd, color }: { onAdd: (entry: RangedYearlyEntry) => void; color: 'blue' | 'red' }) {
  const [amount, setAmount] = useState('')
  const [startYear, setStartYear] = useState(2026)
  const [endYear, setEndYear] = useState('')
  const btnClass = color === 'red' ? 'bg-red-500 hover:bg-red-600' : 'bg-[var(--color-accent)] hover:bg-[var(--color-accent)]/80'
  return (
    <span className="inline-flex items-center gap-1 flex-wrap">
      <input type="number" value={amount} onChange={e => setAmount(e.target.value)} placeholder="$/yr" className="w-20 px-1 py-0.5 text-xs border border-[var(--color-border)] rounded" />
      <select value={startYear} onChange={e => setStartYear(Number(e.target.value))} className="px-1 py-0.5 text-xs border border-[var(--color-border)] rounded">
        {Array.from({ length: PROJECTION_END_YEAR - 2026 + 1 }, (_, i) => 2026 + i).map(y => <option key={y} value={y}>{y}</option>)}
      </select>
      <input type="number" value={endYear} onChange={e => setEndYear(e.target.value)} placeholder="End (opt)" className="w-16 px-1 py-0.5 text-xs border border-[var(--color-border)] rounded" />
      <button onClick={() => { if (Number(amount) > 0) { onAdd({ amount: Number(amount), startYear, endYear: endYear ? Number(endYear) : undefined }); setAmount(''); setEndYear('') } }} className={`px-1.5 py-0.5 text-xs text-white rounded ${btnClass}`}>{color === 'red' ? '−' : '+'}</button>
    </span>
  )
}

function AddRangedMonthlyForm({ onAdd, color }: { onAdd: (entry: RangedMonthlyEntry) => void; color: 'blue' | 'red' }) {
  const [amount, setAmount] = useState('')
  const [startYear, setStartYear] = useState(2026)
  const [startMonth, setStartMonth] = useState(1)
  const [endYear, setEndYear] = useState('')
  const [endMonth, setEndMonth] = useState(12)
  const btnClass = color === 'red' ? 'bg-red-500 hover:bg-red-600' : 'bg-[var(--color-accent)] hover:bg-[var(--color-accent)]/80'
  return (
    <span className="inline-flex items-center gap-1 flex-wrap">
      <input type="number" value={amount} onChange={e => setAmount(e.target.value)} placeholder="$/mo" className="w-16 px-1 py-0.5 text-xs border border-[var(--color-border)] rounded" />
      <select value={startMonth} onChange={e => setStartMonth(Number(e.target.value))} className="px-1 py-0.5 text-xs border border-[var(--color-border)] rounded">
        {Array.from({ length: 12 }, (_, i) => i + 1).map(m => <option key={m} value={m}>{m}</option>)}
      </select>
      <select value={startYear} onChange={e => setStartYear(Number(e.target.value))} className="px-1 py-0.5 text-xs border border-[var(--color-border)] rounded">
        {Array.from({ length: PROJECTION_END_YEAR - 2026 + 1 }, (_, i) => 2026 + i).map(y => <option key={y} value={y}>{y}</option>)}
      </select>
      <span className="text-xs text-[var(--color-text-muted)]">to</span>
      <select value={endMonth} onChange={e => setEndMonth(Number(e.target.value))} className="px-1 py-0.5 text-xs border border-[var(--color-border)] rounded">
        {Array.from({ length: 12 }, (_, i) => i + 1).map(m => <option key={m} value={m}>{m}</option>)}
      </select>
      <input type="number" value={endYear} onChange={e => setEndYear(e.target.value)} placeholder="End yr" className="w-16 px-1 py-0.5 text-xs border border-[var(--color-border)] rounded" />
      <button onClick={() => { if (Number(amount) > 0) { onAdd({ amount: Number(amount), startYear, startMonth, endYear: endYear ? Number(endYear) : undefined, endMonth }); setAmount(''); setEndYear('') } }} className={`px-1.5 py-0.5 text-xs text-white rounded ${btnClass}`}>{color === 'red' ? '−' : '+'}</button>
    </span>
  )
}

function AddOneOffForm({ onAdd, color }: { onAdd: (entry: OneOffEntry) => void; color: 'blue' | 'red' }) {
  const [amount, setAmount] = useState('')
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10))
  const btnClass = color === 'red' ? 'bg-red-500 hover:bg-red-600' : 'bg-[var(--color-accent)] hover:bg-[var(--color-accent)]/80'
  return (
    <span className="inline-flex items-center gap-1">
      <input type="date" value={date} onChange={e => setDate(e.target.value)} className="px-1 py-0.5 text-xs border border-[var(--color-border)] rounded" />
      <input type="number" value={amount} onChange={e => setAmount(e.target.value)} placeholder="Amount" className="w-20 px-1 py-0.5 text-xs border border-[var(--color-border)] rounded" />
      <button onClick={() => { if (Number(amount) > 0) { onAdd({ date, amount: Number(amount) }); setAmount('') } }} className={`px-1.5 py-0.5 text-xs text-white rounded ${btnClass}`}>{color === 'red' ? '−' : '+'}</button>
    </span>
  )
}

// ─── Main Component ──────────────────────────────────────────────────────────────

export function ProjectionsPage() {
  const [projections, setProjections] = useState<AccountProjection[]>(initialProjections)
  const [isLoading, setIsLoading] = useState(true)
  const [collapsedCards, setCollapsedCards] = useState<Set<string>>(new Set())
  const [savedProjections, setSavedProjections] = useState<SavedProjection[]>(loadSavedProjections)
  const [showModal, setShowModal] = useState(false)
  const [configOpenFor, setConfigOpenFor] = useState<string | null>(null)
  const [selectedTotalYear, setSelectedTotalYear] = useState<number>(2041)
  const [expandedAdditionsRows, setExpandedAdditionsRows] = useState<Set<string>>(new Set())
  const [pullGainsModal, setPullGainsModal] = useState<{ accountIdx: number; gains: Record<string, number>; total: number } | null>(null)
  const [pullGainsLoading, setPullGainsLoading] = useState(false)

  // Load projections from Supabase on mount
  const hasLoadedRef = React.useRef(false)
  useEffect(() => {
    async function loadFromSupabase() {
      const { data, error } = await supabase.from('projections').select('*')
      if (error || !data || data.length === 0) {
        hasLoadedRef.current = true
        setIsLoading(false)
        return
      }
      const loaded: AccountProjection[] = initialProjections.map(init => {
        const saved = data.find((d: Record<string, unknown>) => d.account_name === init.name)
        if (!saved) return init
        const migrated = migrateOldProjection(saved)
        const expectedYears = PROJECTION_END_YEAR - 2026 + 1
        const yearsData = saved.years as Array<{ year: number; starting: number; projected: number; actual?: number; tax?: number }> | undefined
        if (!yearsData || yearsData.length < expectedYears) {
          return {
            ...init,
            returnRates: migrated.returnRates || init.returnRates,
            taxEnabled: migrated.taxEnabled ?? (defaultTaxEnabled[init.name] ?? false),
            linkedAccounts: migrated.linkedAccounts || [],
            additions: migrated.additions || undefined,
          }
        }
        const overridesArr = saved.starting_overrides as number[] | undefined
        return {
          name: init.name,
          returnRates: migrated.returnRates || init.returnRates,
          taxEnabled: migrated.taxEnabled ?? (defaultTaxEnabled[init.name] ?? false),
          linkedAccounts: migrated.linkedAccounts || [],
          years: yearsData,
          startingOverrides: overridesArr?.length ? new Set(overridesArr) : undefined,
          additions: migrated.additions || undefined,
        }
      })
      setProjections(loaded)
      hasLoadedRef.current = true
      setIsLoading(false)
    }
    loadFromSupabase()
  }, [])

  // Save to Supabase whenever projections change (debounced 1s)
  const saveTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null)
  useEffect(() => {
    if (!hasLoadedRef.current) return
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current)
    saveTimerRef.current = setTimeout(() => {
      projections.forEach(async (p) => {
        const payload: Record<string, unknown> = {
          account_name: p.name,
          target_return: getEffectiveReturn(p.returnRates, 2026),
          return_rates: p.returnRates,
          tax_enabled: p.taxEnabled,
          linked_accounts: p.linkedAccounts,
          years: p.years,
          starting_overrides: p.startingOverrides ? Array.from(p.startingOverrides) : [],
          additions: p.additions || { yearly: [], monthly: [], manual: [], withdrawals: { yearly: [], monthly: [], oneOff: [] } },
          updated_at: new Date().toISOString(),
        }
        const { error } = await supabase.from('projections').upsert(payload, { onConflict: 'account_name' })
        if (error?.code === 'PGRST204' || error?.message?.includes('column')) {
          // Retry without new fields if columns don't exist yet
          delete payload.return_rates
          delete payload.tax_enabled
          delete payload.linked_accounts
          await supabase.from('projections').upsert(payload, { onConflict: 'account_name' })
        }
      })
    }, 1000)
  }, [projections])

  // Modal state for Create Projection
  const [modalInitial, setModalInitial] = useState<number>(50000)
  const [modalRate, setModalRate] = useState<number>(20)
  const [modalYears, setModalYears] = useState<number>(15)
  const [modalAdditional, setModalAdditional] = useState<AdditionalInvestment[]>([])
  const [modalName, setModalName] = useState('')
  const [modalTable, setModalTable] = useState<{ year: number; starting: number; growth: number; additional: number; ending: number }[]>([])

  useEffect(() => {
    if (showModal) {
      const table = computeProjectionTable(modalInitial, modalRate, modalYears, modalAdditional)
      setModalTable(table)
    }
  }, [showModal, modalInitial, modalRate, modalYears, modalAdditional])

  const toggleCard = (name: string) => {
    setCollapsedCards(prev => {
      const next = new Set(prev)
      if (next.has(name)) next.delete(name); else next.add(name)
      return next
    })
  }

  // ─── Account Edit Handlers ────────────────────────────────────────────────────

  const handleAccountEdit = (accountIdx: number, yearIdx: number, field: 'starting' | 'actual', value: number) => {
    setProjections(prev => {
      const updated = [...prev]
      const account = { ...updated[accountIdx] }
      const years = [...account.years.map(y => ({ ...y }))]
      const overrides = new Set(account.startingOverrides || [])
      years[yearIdx] = { ...years[yearIdx], [field]: value }
      if (field === 'starting') overrides.add(yearIdx)
      account.years = years
      account.startingOverrides = overrides
      updated[accountIdx] = recalculateAllYears(account)
      return updated
    })
  }

  const handleReturnRateAdd = (accountIdx: number, entry: ReturnRateEntry) => {
    setProjections(prev => {
      const updated = [...prev]
      const account = { ...updated[accountIdx] }
      account.returnRates = [...account.returnRates, entry]
      updated[accountIdx] = recalculateAllYears(account)
      return updated
    })
  }

  const handleReturnRateRemove = (accountIdx: number, rateIdx: number) => {
    setProjections(prev => {
      const updated = [...prev]
      const account = { ...updated[accountIdx] }
      account.returnRates = account.returnRates.filter((_, i) => i !== rateIdx)
      if (account.returnRates.length === 0) account.returnRates = [{ rate: 10, startYear: 2026 }]
      updated[accountIdx] = recalculateAllYears(account)
      return updated
    })
  }

  const handleTaxToggle = (accountIdx: number) => {
    setProjections(prev => {
      const updated = [...prev]
      const account = { ...updated[accountIdx], taxEnabled: !updated[accountIdx].taxEnabled }
      updated[accountIdx] = recalculateAllYears(account)
      return updated
    })
  }

  const handleLinkedAccountToggle = (accountIdx: number, slug: string) => {
    setProjections(prev => {
      const updated = [...prev]
      const account = { ...updated[accountIdx] }
      const linked = [...account.linkedAccounts]
      const idx = linked.indexOf(slug)
      if (idx >= 0) linked.splice(idx, 1); else linked.push(slug)
      account.linkedAccounts = linked
      updated[accountIdx] = account
      return updated
    })
  }

  // ─── Additions/Withdrawals Handlers ─────────────────────────────────────────────

  const ensureAdditions = (account: AccountProjection): AccountAdditions => {
    return account.additions || { yearly: [], monthly: [], manual: [], withdrawals: { yearly: [], monthly: [], oneOff: [] } }
  }

  const handleAddYearly = (accountIdx: number, entry: RangedYearlyEntry) => {
    setProjections(prev => {
      const updated = [...prev]
      const account = { ...updated[accountIdx] }
      const additions = { ...ensureAdditions(account) }
      additions.yearly = [...additions.yearly, entry]
      account.additions = additions
      updated[accountIdx] = recalculateAllYears(account)
      return updated
    })
  }

  const handleRemoveYearly = (accountIdx: number, entryIdx: number) => {
    setProjections(prev => {
      const updated = [...prev]
      const account = { ...updated[accountIdx] }
      const additions = { ...ensureAdditions(account) }
      additions.yearly = additions.yearly.filter((_, i) => i !== entryIdx)
      account.additions = additions
      updated[accountIdx] = recalculateAllYears(account)
      return updated
    })
  }

  const handleAddMonthly = (accountIdx: number, entry: RangedMonthlyEntry) => {
    setProjections(prev => {
      const updated = [...prev]
      const account = { ...updated[accountIdx] }
      const additions = { ...ensureAdditions(account) }
      additions.monthly = [...additions.monthly, entry]
      account.additions = additions
      updated[accountIdx] = recalculateAllYears(account)
      return updated
    })
  }

  const handleRemoveMonthly = (accountIdx: number, entryIdx: number) => {
    setProjections(prev => {
      const updated = [...prev]
      const account = { ...updated[accountIdx] }
      const additions = { ...ensureAdditions(account) }
      additions.monthly = additions.monthly.filter((_, i) => i !== entryIdx)
      account.additions = additions
      updated[accountIdx] = recalculateAllYears(account)
      return updated
    })
  }

  const handleAddManual = (accountIdx: number, entry: OneOffEntry) => {
    setProjections(prev => {
      const updated = [...prev]
      const account = { ...updated[accountIdx] }
      const additions = { ...ensureAdditions(account) }
      additions.manual = [...additions.manual, entry]
      account.additions = additions
      updated[accountIdx] = recalculateAllYears(account)
      return updated
    })
  }

  const handleRemoveManual = (accountIdx: number, entryIdx: number) => {
    setProjections(prev => {
      const updated = [...prev]
      const account = { ...updated[accountIdx] }
      const additions = { ...ensureAdditions(account) }
      additions.manual = additions.manual.filter((_, i) => i !== entryIdx)
      account.additions = additions
      updated[accountIdx] = recalculateAllYears(account)
      return updated
    })
  }

  const handleAddWithdrawalYearly = (accountIdx: number, entry: RangedYearlyEntry) => {
    setProjections(prev => {
      const updated = [...prev]
      const account = { ...updated[accountIdx] }
      const additions = { ...ensureAdditions(account) }
      additions.withdrawals = { ...additions.withdrawals, yearly: [...additions.withdrawals.yearly, entry] }
      account.additions = additions
      updated[accountIdx] = recalculateAllYears(account)
      return updated
    })
  }

  const handleRemoveWithdrawalYearly = (accountIdx: number, entryIdx: number) => {
    setProjections(prev => {
      const updated = [...prev]
      const account = { ...updated[accountIdx] }
      const additions = { ...ensureAdditions(account) }
      additions.withdrawals = { ...additions.withdrawals, yearly: additions.withdrawals.yearly.filter((_, i) => i !== entryIdx) }
      account.additions = additions
      updated[accountIdx] = recalculateAllYears(account)
      return updated
    })
  }

  const handleAddWithdrawalMonthly = (accountIdx: number, entry: RangedMonthlyEntry) => {
    setProjections(prev => {
      const updated = [...prev]
      const account = { ...updated[accountIdx] }
      const additions = { ...ensureAdditions(account) }
      additions.withdrawals = { ...additions.withdrawals, monthly: [...additions.withdrawals.monthly, entry] }
      account.additions = additions
      updated[accountIdx] = recalculateAllYears(account)
      return updated
    })
  }

  const handleRemoveWithdrawalMonthly = (accountIdx: number, entryIdx: number) => {
    setProjections(prev => {
      const updated = [...prev]
      const account = { ...updated[accountIdx] }
      const additions = { ...ensureAdditions(account) }
      additions.withdrawals = { ...additions.withdrawals, monthly: additions.withdrawals.monthly.filter((_, i) => i !== entryIdx) }
      account.additions = additions
      updated[accountIdx] = recalculateAllYears(account)
      return updated
    })
  }

  const handleAddWithdrawalOneOff = (accountIdx: number, entry: OneOffEntry) => {
    setProjections(prev => {
      const updated = [...prev]
      const account = { ...updated[accountIdx] }
      const additions = { ...ensureAdditions(account) }
      additions.withdrawals = { ...additions.withdrawals, oneOff: [...additions.withdrawals.oneOff, entry] }
      account.additions = additions
      updated[accountIdx] = recalculateAllYears(account)
      return updated
    })
  }

  const handleRemoveWithdrawalOneOff = (accountIdx: number, entryIdx: number) => {
    setProjections(prev => {
      const updated = [...prev]
      const account = { ...updated[accountIdx] }
      const additions = { ...ensureAdditions(account) }
      additions.withdrawals = { ...additions.withdrawals, oneOff: additions.withdrawals.oneOff.filter((_, i) => i !== entryIdx) }
      account.additions = additions
      updated[accountIdx] = recalculateAllYears(account)
      return updated
    })
  }

  // ─── Pull Gains ────────────────────────────────────────────────────────────────

  const handlePullGains = async (accountIdx: number) => {
    const account = projections[accountIdx]
    if (account.linkedAccounts.length === 0) return
    setPullGainsLoading(true)
    const currentYear = new Date().getFullYear()
    const perAccount: Record<string, number> = {}
    let total = 0
    for (const slug of account.linkedAccounts) {
      const { data } = await supabase
        .from('trades')
        .select('net_position')
        .eq('account_slug', slug)
        .eq('status', 'closed')
        .gte('close_date', `${currentYear}-01-01`)
        .lte('close_date', `${currentYear}-12-31`)
      const sum = (data || []).reduce((s: number, t: { net_position: number | null }) => s + (t.net_position ?? 0), 0)
      perAccount[slug] = sum
      total += sum
    }
    setPullGainsLoading(false)
    setPullGainsModal({ accountIdx, gains: perAccount, total })
  }

  const handleApplyGains = () => {
    if (!pullGainsModal) return
    const { accountIdx, total } = pullGainsModal
    setProjections(prev => {
      const updated = [...prev]
      const account = { ...updated[accountIdx] }
      const years = [...account.years.map(y => ({ ...y }))]
      const currentYear = new Date().getFullYear()
      const yearIdx = years.findIndex(y => y.year === currentYear)
      if (yearIdx >= 0) {
        const netAdd = getNetAdditionsForYear(account.additions, currentYear)
        years[yearIdx].actual = Math.round(years[yearIdx].starting + netAdd + total)
      }
      account.years = years
      updated[accountIdx] = recalculateAllYears(account)
      return updated
    })
    setPullGainsModal(null)
  }

  // ─── Delete Account ────────────────────────────────────────────────────────────

  const handleDeleteAccount = async (accountIdx: number) => {
    const account = projections[accountIdx]
    if (!confirm(`Delete "${account.name}" projection? This cannot be undone.`)) return
    await supabase.from('projections').delete().eq('account_name', account.name)
    setProjections(prev => prev.filter((_, i) => i !== accountIdx))
  }

  // ─── Modal Handlers ────────────────────────────────────────────────────────────

  const handleAddInvestment = () => setModalAdditional(prev => [...prev, { year: 1, amount: 0 }])
  const handleRemoveInvestment = (idx: number) => setModalAdditional(prev => prev.filter((_, i) => i !== idx))

  const resetModal = () => {
    setModalInitial(50000)
    setModalRate(20)
    setModalYears(15)
    setModalAdditional([])
    setModalName('')
    setModalTable([])
  }

  const openModal = () => { resetModal(); setShowModal(true) }

  const handleSaveProjection = () => {
    if (!modalName.trim()) return
    const newProjection: SavedProjection = {
      id: Date.now().toString(),
      name: modalName.trim(),
      initialInvestment: modalInitial,
      growthRate: modalRate,
      numYears: modalYears,
      additionalInvestments: modalAdditional,
      table: modalTable,
    }
    const updated = [...savedProjections, newProjection]
    setSavedProjections(updated)
    saveSavedProjections(updated)
    setShowModal(false)
    resetModal()
  }

  const handleDeleteSaved = (id: string) => {
    const updated = savedProjections.filter(p => p.id !== id)
    setSavedProjections(updated)
    saveSavedProjections(updated)
  }

  // ─── Computed Values ───────────────────────────────────────────────────────────

  const yearTotal = projections.reduce((sum, account) => {
    const row = account.years.find(y => y.year === selectedTotalYear)
    if (!row) return sum
    const value = (row.actual && row.actual > 0) ? row.actual : row.projected
    return sum + value
  }, 0)

  // ─── Render ────────────────────────────────────────────────────────────────────

  return (
    <div className="h-full flex flex-col">
      {isLoading && (
        <div className="flex-1 flex items-center justify-center">
          <p className="text-sm text-[var(--color-text-muted)]">Loading projections...</p>
        </div>
      )}
      {!isLoading && (<>
      {/* Sticky header */}
      <div className="sticky top-0 z-10 bg-white border-b border-[var(--color-border)] px-6 pt-4 pb-3">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-[var(--color-text-primary)]">Projections</h1>
            <p className="text-sm text-[var(--color-text-muted)]">Compound growth projections across all accounts</p>
          </div>
          <button onClick={openModal} className="px-5 py-2.5 text-sm font-medium rounded-lg bg-[var(--color-accent)] text-white hover:bg-[var(--color-accent)]/80" style={{ height: '44px' }}>
            📈 Create Projection
          </button>
        </div>
        <div className="flex items-center gap-3 mt-3 pt-3 border-t border-[var(--color-border)]">
          <label className="text-sm font-medium text-[var(--color-text-secondary)]">Total for year:</label>
          <select value={selectedTotalYear} onChange={(e) => setSelectedTotalYear(Number(e.target.value))} className="px-3 py-1.5 text-sm rounded-md border border-[var(--color-border)] bg-white text-[var(--color-text-primary)]">
            {Array.from({ length: PROJECTION_END_YEAR - 2026 + 1 }, (_, i) => 2026 + i).map(yr => (
              <option key={yr} value={yr}>{yr}</option>
            ))}
          </select>
          <span className="text-lg font-bold text-[var(--color-accent)] font-mono">{formatCurrency(yearTotal)}</span>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto px-6 py-4 space-y-4">
        {/* Account Projection Cards */}
        {projections.map((account, accountIdx) => {
          const effectiveRate = getEffectiveReturn(account.returnRates, new Date().getFullYear())
          return (
          <div key={account.name} className="border border-[var(--color-border)] rounded-lg overflow-hidden">
            {/* Card Header */}
            <div className="flex items-center justify-between px-4 py-3 bg-[var(--color-bg-tertiary)] cursor-pointer hover:bg-[var(--color-bg-tertiary)]/80" onClick={() => toggleCard(account.name)}>
              <div className="flex items-center gap-3">
                <span className="text-sm font-medium text-[var(--color-text-muted)]">{collapsedCards.has(account.name) ? '▶' : '▼'}</span>
                <h2 className="text-base font-semibold text-[var(--color-text-primary)]">{account.name}</h2>
              </div>
              <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                <button
                  onClick={() => setConfigOpenFor(configOpenFor === account.name ? null : account.name)}
                  className={`px-2 py-1 text-xs rounded ${configOpenFor === account.name ? 'bg-[var(--color-accent)] text-white' : 'bg-[var(--color-bg-tertiary)] text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)]'}`}
                  title="Configure projection settings"
                >⚙️</button>
                <button
                  onClick={() => handlePullGains(accountIdx)}
                  disabled={account.linkedAccounts.length === 0 || pullGainsLoading}
                  className={`px-2 py-1 text-xs rounded ${account.linkedAccounts.length === 0 ? 'opacity-40 cursor-not-allowed' : 'hover:bg-[var(--color-bg-tertiary)]'} bg-[var(--color-bg-tertiary)] text-[var(--color-text-muted)]`}
                  title={account.linkedAccounts.length === 0 ? 'Configure linked accounts in settings first' : 'Pull realized gains from trade accounts'}
                >📊</button>
                <button onClick={() => handleDeleteAccount(accountIdx)} className="px-2 py-1 text-xs rounded bg-[var(--color-bg-tertiary)] text-[var(--color-text-muted)] hover:bg-red-100 hover:text-red-700" title="Delete this projection">🗑️</button>
                <span className="text-sm text-[var(--color-text-muted)]">Target Return:</span>
                <span className="font-semibold text-[var(--color-accent)] text-sm">{effectiveRate}%</span>
              </div>
            </div>

            {/* Card body */}
            {!collapsedCards.has(account.name) && (
              <>
                {/* Config Panel */}
                {configOpenFor === account.name && (
                  <div className="px-4 py-3 border-b border-[var(--color-border)]">
                    {/* 3-column grid */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
                      {/* Column 1: Additions (blue) */}
                      <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                        <h3 className="text-sm font-semibold text-blue-800 mb-2">💰 Additions</h3>
                        <div className="mb-2">
                          <p className="text-xs font-medium text-blue-600 mb-1">Yearly:</p>
                          <div className="flex flex-wrap gap-1 mb-1">
                            {(account.additions?.yearly || []).map((entry, i) => (
                              <RangedYearlyPill key={i} entry={entry} onRemove={() => handleRemoveYearly(accountIdx, i)} color="blue" />
                            ))}
                          </div>
                          <AddRangedYearlyForm onAdd={(e) => handleAddYearly(accountIdx, e)} color="blue" />
                        </div>
                        <div className="mb-2">
                          <p className="text-xs font-medium text-blue-600 mb-1">Monthly:</p>
                          <div className="flex flex-wrap gap-1 mb-1">
                            {(account.additions?.monthly || []).map((entry, i) => (
                              <RangedMonthlyPill key={i} entry={entry} onRemove={() => handleRemoveMonthly(accountIdx, i)} color="blue" />
                            ))}
                          </div>
                          <AddRangedMonthlyForm onAdd={(e) => handleAddMonthly(accountIdx, e)} color="blue" />
                        </div>
                        <div>
                          <p className="text-xs font-medium text-blue-600 mb-1">One-off:</p>
                          <div className="flex flex-wrap gap-1 mb-1">
                            {(account.additions?.manual || []).map((entry, i) => (
                              <OneOffPill key={i} entry={entry} onRemove={() => handleRemoveManual(accountIdx, i)} color="blue" />
                            ))}
                          </div>
                          <AddOneOffForm onAdd={(e) => handleAddManual(accountIdx, e)} color="blue" />
                        </div>
                      </div>

                      {/* Column 2: Withdrawals (red) */}
                      <div className="p-3 bg-red-50 rounded-lg border border-red-200">
                        <h3 className="text-sm font-semibold text-red-800 mb-2">💸 Withdrawals</h3>
                        <div className="mb-2">
                          <p className="text-xs font-medium text-red-600 mb-1">Yearly:</p>
                          <div className="flex flex-wrap gap-1 mb-1">
                            {(account.additions?.withdrawals?.yearly || []).map((entry, i) => (
                              <RangedYearlyPill key={i} entry={entry} onRemove={() => handleRemoveWithdrawalYearly(accountIdx, i)} color="red" />
                            ))}
                          </div>
                          <AddRangedYearlyForm onAdd={(e) => handleAddWithdrawalYearly(accountIdx, e)} color="red" />
                        </div>
                        <div className="mb-2">
                          <p className="text-xs font-medium text-red-600 mb-1">Monthly:</p>
                          <div className="flex flex-wrap gap-1 mb-1">
                            {(account.additions?.withdrawals?.monthly || []).map((entry, i) => (
                              <RangedMonthlyPill key={i} entry={entry} onRemove={() => handleRemoveWithdrawalMonthly(accountIdx, i)} color="red" />
                            ))}
                          </div>
                          <AddRangedMonthlyForm onAdd={(e) => handleAddWithdrawalMonthly(accountIdx, e)} color="red" />
                        </div>
                        <div>
                          <p className="text-xs font-medium text-red-600 mb-1">One-off:</p>
                          <div className="flex flex-wrap gap-1 mb-1">
                            {(account.additions?.withdrawals?.oneOff || []).map((entry, i) => (
                              <OneOffPill key={i} entry={entry} onRemove={() => handleRemoveWithdrawalOneOff(accountIdx, i)} color="red" />
                            ))}
                          </div>
                          <AddOneOffForm onAdd={(e) => handleAddWithdrawalOneOff(accountIdx, e)} color="red" />
                        </div>
                      </div>

                      {/* Column 3: Return Rates (purple) */}
                      <div className="p-3 bg-purple-50 rounded-lg border border-purple-200">
                        <h3 className="text-sm font-semibold text-purple-800 mb-2">📈 Return Rates</h3>
                        <div className="flex flex-wrap gap-1 mb-2">
                          {account.returnRates.map((entry, i) => (
                            <RateEntryPill key={i} entry={entry} onRemove={() => handleReturnRateRemove(accountIdx, i)} />
                          ))}
                        </div>
                        <AddRateEntryForm onAdd={(e) => handleReturnRateAdd(accountIdx, e)} />
                      </div>
                    </div>

                    {/* Settings row */}
                    <div className="p-3 bg-[var(--color-bg-tertiary)] rounded-lg border border-[var(--color-border)]">
                      <div className="flex flex-wrap items-center gap-4">
                        <label className="inline-flex items-center gap-2 text-xs">
                          <input type="checkbox" checked={account.taxEnabled} onChange={() => handleTaxToggle(accountIdx)} className="rounded" />
                          <span className="text-[var(--color-text-secondary)]">Tax enabled (35% on gains)</span>
                        </label>
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-[var(--color-text-muted)]">Linked accounts:</span>
                          <div className="flex flex-wrap gap-1">
                            {ALL_ACCOUNT_SLUGS.map(slug => (
                              <label key={slug} className="inline-flex items-center gap-1 text-xs">
                                <input type="checkbox" checked={account.linkedAccounts.includes(slug)} onChange={() => handleLinkedAccountToggle(accountIdx, slug)} className="rounded" />
                                <span className="text-[var(--color-text-muted)]">{slug}</span>
                              </label>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Table */}
                <div className="overflow-y-auto max-h-[320px] overflow-x-auto">
                <table className="w-full text-sm border-collapse">
                  <thead className="bg-[var(--color-bg-tertiary)]/50 sticky top-0">
                    <tr>
                      <th className="px-4 py-2.5 text-left text-xs font-medium text-[var(--color-text-muted)] uppercase">Year</th>
                      <th className="px-4 py-2.5 text-left text-xs font-medium text-[var(--color-text-muted)] uppercase">Starting Amount</th>
                      <th className="px-4 py-2.5 text-left text-xs font-medium text-[var(--color-text-muted)] uppercase">Added</th>
                      <th className="px-4 py-2.5 text-left text-xs font-medium text-[var(--color-text-muted)] uppercase">Projected</th>
                      <th className="px-4 py-2.5 text-left text-xs font-medium text-[var(--color-text-muted)] uppercase">Est. Tax</th>
                      <th className="px-4 py-2.5 text-left text-xs font-medium text-[var(--color-text-muted)] uppercase">Actual</th>
                    </tr>
                  </thead>
                  <tbody>
                    {account.years.map((row, yearIdx) => {
                      const hasActual = row.actual !== undefined && row.actual !== null && row.actual > 0
                      const actualColor = hasActual
                        ? (row.actual! >= row.projected ? 'text-[var(--color-green)]' : 'text-[var(--color-red)]')
                        : ''
                      const isHighlighted = HIGHLIGHT_YEARS.includes(row.year)
                      const annualAdded = getTotalAdditionsForYear(account.additions, row.year)
                      const annualWithdrawn = getTotalWithdrawalsForYear(account.additions, row.year)
                      const netAdded = annualAdded - annualWithdrawn
                      const rowKey = `${account.name}-${row.year}`
                      const isExpanded = expandedAdditionsRows.has(rowKey)

                      return (
                        <React.Fragment key={row.year}>
                        <tr
                          className={`border-t border-[var(--color-border)] hover:bg-[var(--color-bg-tertiary)]/30 ${isHighlighted ? 'bg-amber-50 border-l-4 border-l-amber-400' : ''}`}
                          style={{ height: '44px' }}
                        >
                          <td className={`px-4 py-2 font-medium ${isHighlighted ? 'text-amber-800' : 'text-[var(--color-text-primary)]'}`}>{row.year}</td>
                          <td className="px-4 py-2 font-mono">
                            <InlineEdit
                              value={row.starting}
                              fieldName={`starting-${accountIdx}-${yearIdx}`}
                              type="number"
                              onSave={(v) => handleAccountEdit(accountIdx, yearIdx, 'starting', Number(v))}
                              displayFormatter={(v) => formatCurrency(Number(v))}
                              className="font-mono"
                            />
                          </td>
                          <td className="px-4 py-2 font-mono text-[var(--color-accent)]">
                            {netAdded !== 0 ? (
                              <button
                                onClick={() => setExpandedAdditionsRows(prev => {
                                  const next = new Set(prev)
                                  if (next.has(rowKey)) next.delete(rowKey); else next.add(rowKey)
                                  return next
                                })}
                                className="hover:underline cursor-pointer"
                                title="Click to see breakdown"
                              >
                                {netAdded >= 0 ? '+' : ''}{formatCurrency(netAdded)} {isExpanded ? '▾' : '▸'}
                              </button>
                            ) : '—'}
                          </td>
                          <td className="px-4 py-2 font-mono text-[var(--color-text-secondary)]">{formatCurrency(row.projected)}</td>
                          <td className="px-4 py-2 font-mono text-amber-600">
                            {account.taxEnabled ? (row.tax ? formatCurrency(row.tax) : '$0') : '—'}
                          </td>
                          <td className={`px-4 py-2 font-mono ${actualColor}`}>
                            <InlineEdit
                              value={row.actual ?? ''}
                              fieldName={`actual-${accountIdx}-${yearIdx}`}
                              type="number"
                              onSave={(v) => handleAccountEdit(accountIdx, yearIdx, 'actual', Number(v))}
                              displayFormatter={(v) => v ? formatCurrency(Number(v)) : '—'}
                              className={`font-mono ${actualColor}`}
                            />
                          </td>
                        </tr>
                        {/* Expanded additions breakdown */}
                        {isExpanded && netAdded !== 0 && (
                          <tr className="bg-blue-50/50">
                            <td colSpan={6} className="px-8 py-2">
                              <div className="text-xs space-y-0.5">
                                {getYearlyTotal(account.additions?.yearly || [], row.year) > 0 && (
                                  <div className="text-[var(--color-text-muted)]">
                                    📅 Yearly: <span className="font-medium">{formatCurrency(getYearlyTotal(account.additions?.yearly || [], row.year))}</span>
                                  </div>
                                )}
                                {getMonthlyDetails(account.additions?.monthly || [], row.year).totalForYear > 0 && (
                                  <div className="text-[var(--color-text-muted)]">
                                    📅 Monthly: <span className="font-medium">{formatCurrency(getMonthlyDetails(account.additions?.monthly || [], row.year).totalForYear)}</span>
                                    {' '}({getMonthlyDetails(account.additions?.monthly || [], row.year).activeMonths.length} months active)
                                  </div>
                                )}
                                {getOneOffsForYear(account.additions?.manual || [], row.year).map((entry, i) => (
                                  <div key={i} className="text-[var(--color-text-muted)]">
                                    💵 {entry.date}: <span className="font-medium">{formatCurrency(entry.amount)}</span>
                                  </div>
                                ))}
                                {annualWithdrawn > 0 && (
                                  <div className="text-red-600">
                                    💸 Withdrawals: <span className="font-medium">-{formatCurrency(annualWithdrawn)}</span>
                                  </div>
                                )}
                                <div className="font-medium text-[var(--color-accent)] pt-1 border-t border-[var(--color-border)]">
                                  Net: {formatCurrency(netAdded)}
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                        </React.Fragment>
                      )
                    })}
                  </tbody>
                </table>
                </div>
              </>
            )}
          </div>
          )
        })}

        {/* Saved Custom Projections */}
        {savedProjections.length > 0 && (
          <div className="mt-8">
            <h2 className="text-lg font-semibold text-[var(--color-text-primary)] mb-4">Saved Projections</h2>
            <div className="space-y-4">
              {savedProjections.map(proj => (
                <div key={proj.id} className="border border-[var(--color-border)] rounded-lg overflow-hidden">
                  <div className="flex items-center justify-between px-4 py-3 bg-[var(--color-bg-tertiary)]">
                    <div>
                      <h3 className="text-base font-semibold text-[var(--color-text-primary)]">{proj.name}</h3>
                      <p className="text-xs text-[var(--color-text-muted)]">
                        {formatCurrency(proj.initialInvestment)} @ {proj.growthRate}% for {proj.numYears} years
                        {proj.additionalInvestments.length > 0 && ` + ${proj.additionalInvestments.length} additional investment(s)`}
                      </p>
                    </div>
                    <button onClick={() => handleDeleteSaved(proj.id)} className="px-3 py-1.5 text-xs rounded bg-red-100 text-red-700 hover:bg-red-200 font-medium">Delete</button>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm border-collapse">
                      <thead className="bg-[var(--color-bg-tertiary)]/50">
                        <tr>
                          <th className="px-4 py-2.5 text-left text-xs font-medium text-[var(--color-text-muted)] uppercase">Year</th>
                          <th className="px-4 py-2.5 text-left text-xs font-medium text-[var(--color-text-muted)] uppercase">Starting</th>
                          <th className="px-4 py-2.5 text-left text-xs font-medium text-[var(--color-text-muted)] uppercase">Growth</th>
                          <th className="px-4 py-2.5 text-left text-xs font-medium text-[var(--color-text-muted)] uppercase">Additional</th>
                          <th className="px-4 py-2.5 text-left text-xs font-medium text-[var(--color-text-muted)] uppercase">Ending</th>
                        </tr>
                      </thead>
                      <tbody>
                        {proj.table.map((row) => (
                          <tr key={row.year} className="border-t border-[var(--color-border)] hover:bg-[var(--color-bg-tertiary)]/30" style={{ height: '44px' }}>
                            <td className="px-4 py-2 font-medium text-[var(--color-text-primary)]">{row.year}</td>
                            <td className="px-4 py-2 font-mono">{formatCurrency(row.starting)}</td>
                            <td className="px-4 py-2 font-mono text-[var(--color-green)]">+{formatCurrency(row.growth)}</td>
                            <td className="px-4 py-2 font-mono text-[var(--color-accent)]">{row.additional > 0 ? `+${formatCurrency(row.additional)}` : '—'}</td>
                            <td className="px-4 py-2 font-mono font-medium">{formatCurrency(row.ending)}</td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr className="border-t-2 border-[var(--color-border)] bg-[var(--color-bg-tertiary)]/30">
                          <td colSpan={4} className="px-4 py-2 font-semibold text-[var(--color-text-primary)]">Final Value</td>
                          <td className="px-4 py-2 font-mono font-bold text-[var(--color-green)]">
                            {proj.table.length > 0 ? formatCurrency(proj.table[proj.table.length - 1].ending) : '—'}
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Pull Gains Modal */}
      {pullGainsModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => setPullGainsModal(null)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-6 p-6" onClick={(e) => e.stopPropagation()}>
            <h2 className="text-lg font-bold text-[var(--color-text-primary)] mb-4">
              📊 Pull Realized Gains — {projections[pullGainsModal.accountIdx].name}
            </h2>
            <table className="w-full text-sm mb-4">
              <thead>
                <tr className="border-b border-[var(--color-border)]">
                  <th className="text-left py-2 text-xs font-medium text-[var(--color-text-muted)]">Account</th>
                  <th className="text-right py-2 text-xs font-medium text-[var(--color-text-muted)]">Net P&L ({new Date().getFullYear()} YTD)</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(pullGainsModal.gains).map(([slug, pnl]) => (
                  <tr key={slug} className="border-b border-[var(--color-border)]">
                    <td className="py-2 text-[var(--color-text-primary)]">{slug}</td>
                    <td className={`py-2 text-right font-mono ${pnl >= 0 ? 'text-[var(--color-green)]' : 'text-[var(--color-red)]'}`}>{formatCurrency(pnl)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-[var(--color-border)]">
                  <td className="py-2 font-semibold">TOTAL</td>
                  <td className={`py-2 text-right font-mono font-bold ${pullGainsModal.total >= 0 ? 'text-[var(--color-green)]' : 'text-[var(--color-red)]'}`}>{formatCurrency(pullGainsModal.total)}</td>
                </tr>
              </tfoot>
            </table>
            <p className="text-xs text-[var(--color-text-muted)] mb-4">
              This will set {new Date().getFullYear()} Actual to: Starting ({formatCurrency(projections[pullGainsModal.accountIdx].years.find(y => y.year === new Date().getFullYear())?.starting ?? 0)})
              + Net Additions ({formatCurrency(getNetAdditionsForYear(projections[pullGainsModal.accountIdx].additions, new Date().getFullYear()))})
              + Gains ({formatCurrency(pullGainsModal.total)})
              = <strong>{formatCurrency(
                (projections[pullGainsModal.accountIdx].years.find(y => y.year === new Date().getFullYear())?.starting ?? 0)
                + getNetAdditionsForYear(projections[pullGainsModal.accountIdx].additions, new Date().getFullYear())
                + pullGainsModal.total
              )}</strong>
            </p>
            <div className="flex justify-end gap-3">
              <button onClick={() => setPullGainsModal(null)} className="px-4 py-2 text-sm rounded-lg border border-[var(--color-border)] text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-tertiary)]">Cancel</button>
              <button onClick={handleApplyGains} className="px-4 py-2 text-sm rounded-lg bg-[var(--color-accent)] text-white hover:bg-[var(--color-accent)]/80">Apply as Actual</button>
            </div>
          </div>
        </div>
      )}

      {/* Create Projection Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-start justify-center pt-8 bg-black/40" onClick={() => setShowModal(false)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto mx-6" onClick={(e) => e.stopPropagation()}>
            <div className="px-10 pt-8 pb-4">
              <h2 className="text-xl font-bold text-[var(--color-text-primary)]">📈 Create Projection</h2>
              <p className="text-sm text-[var(--color-text-muted)] mt-1">Model compound growth with optional additional investments</p>
            </div>
            <div className="px-10 pb-6 space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[var(--color-text-secondary)] mb-1">Initial Investment *</label>
                  <input type="number" value={modalInitial} onChange={(e) => setModalInitial(Number(e.target.value))} className="w-full px-4 py-2.5 rounded-lg border border-[var(--color-border)] bg-white text-[var(--color-text-primary)] focus:outline-none focus:border-[var(--color-accent)]" placeholder="50000" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[var(--color-text-secondary)] mb-1">Yearly Growth Rate % *</label>
                  <input type="number" value={modalRate} onChange={(e) => setModalRate(Number(e.target.value))} className="w-full px-4 py-2.5 rounded-lg border border-[var(--color-border)] bg-white text-[var(--color-text-primary)] focus:outline-none focus:border-[var(--color-accent)]" placeholder="20" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[var(--color-text-secondary)] mb-1">Number of Years</label>
                  <input type="number" value={modalYears} onChange={(e) => setModalYears(Number(e.target.value))} className="w-full px-4 py-2.5 rounded-lg border border-[var(--color-border)] bg-white text-[var(--color-text-primary)] focus:outline-none focus:border-[var(--color-accent)]" placeholder="15" min={1} max={50} />
                </div>
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-medium text-[var(--color-text-secondary)]">Additional Investments</label>
                  <button onClick={handleAddInvestment} className="px-3 py-1.5 text-xs font-medium rounded-lg border border-[var(--color-border)] text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-tertiary)]">+ Add Investment</button>
                </div>
                {modalAdditional.length > 0 && (
                  <div className="space-y-2">
                    {modalAdditional.map((inv, idx) => (
                      <div key={idx} className="flex items-center gap-3">
                        <select value={inv.year} onChange={(e) => { const u = [...modalAdditional]; u[idx] = { ...u[idx], year: Number(e.target.value) }; setModalAdditional(u) }} className="px-3 py-2 rounded-lg border border-[var(--color-border)] bg-white text-[var(--color-text-primary)] text-sm">
                          {Array.from({ length: modalYears }, (_, i) => i + 1).map(y => <option key={y} value={y}>Year {y}</option>)}
                        </select>
                        <input type="number" value={inv.amount} onChange={(e) => { const u = [...modalAdditional]; u[idx] = { ...u[idx], amount: Number(e.target.value) }; setModalAdditional(u) }} placeholder="Amount" className="flex-1 px-3 py-2 rounded-lg border border-[var(--color-border)] bg-white text-[var(--color-text-primary)] text-sm focus:outline-none focus:border-[var(--color-accent)]" />
                        <button onClick={() => handleRemoveInvestment(idx)} className="px-2 py-1.5 text-xs rounded bg-red-100 text-red-700 hover:bg-red-200">✕</button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              {modalTable.length > 0 && (
                <div className="overflow-x-auto border border-[var(--color-border)] rounded-lg">
                  <table className="w-full text-sm border-collapse">
                    <thead className="bg-[var(--color-bg-tertiary)]">
                      <tr>
                        <th className="px-4 py-2.5 text-left text-xs font-medium text-[var(--color-text-muted)] uppercase">Year</th>
                        <th className="px-4 py-2.5 text-left text-xs font-medium text-[var(--color-text-muted)] uppercase">Starting</th>
                        <th className="px-4 py-2.5 text-left text-xs font-medium text-[var(--color-text-muted)] uppercase">Growth</th>
                        <th className="px-4 py-2.5 text-left text-xs font-medium text-[var(--color-text-muted)] uppercase">Additional</th>
                        <th className="px-4 py-2.5 text-left text-xs font-medium text-[var(--color-text-muted)] uppercase">Ending</th>
                      </tr>
                    </thead>
                    <tbody>
                      {modalTable.map((row) => (
                        <tr key={row.year} className="border-t border-[var(--color-border)] hover:bg-[var(--color-bg-tertiary)]/30" style={{ height: '40px' }}>
                          <td className="px-4 py-2 font-medium text-[var(--color-text-primary)]">{row.year}</td>
                          <td className="px-4 py-2 font-mono">{formatCurrency(Math.round(row.starting))}</td>
                          <td className="px-4 py-2 font-mono text-[var(--color-green)]">+{formatCurrency(Math.round(row.growth))}</td>
                          <td className="px-4 py-2 font-mono text-[var(--color-accent)]">{row.additional > 0 ? `+${formatCurrency(row.additional)}` : '—'}</td>
                          <td className="px-4 py-2 font-mono font-medium">{formatCurrency(Math.round(row.ending))}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="border-t-2 border-[var(--color-border)] bg-[var(--color-bg-tertiary)]/30">
                        <td colSpan={4} className="px-4 py-2 font-semibold text-[var(--color-text-primary)]">Final Value</td>
                        <td className="px-4 py-2 font-mono font-bold text-[var(--color-green)]">{formatCurrency(Math.round(modalTable[modalTable.length - 1].ending))}</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}
              <div className="flex items-center gap-3">
                <input type="text" value={modalName} onChange={(e) => setModalName(e.target.value)} placeholder="Projection name (e.g., Retirement Plan)" className="flex-1 px-4 py-2.5 rounded-lg border border-[var(--color-border)] bg-white text-[var(--color-text-primary)] focus:outline-none focus:border-[var(--color-accent)]" />
              </div>
            </div>
            <div className="px-10 py-4 border-t border-[var(--color-border)] flex justify-end gap-3">
              <button onClick={() => setShowModal(false)} className="px-5 py-2.5 text-sm font-medium rounded-lg border border-[var(--color-border)] text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-tertiary)]">Cancel</button>
              <button onClick={handleSaveProjection} disabled={!modalName.trim() || modalTable.length === 0} className="px-5 py-2.5 text-sm font-medium rounded-lg bg-[var(--color-accent)] text-white hover:bg-[var(--color-accent)]/80 disabled:opacity-50 disabled:cursor-not-allowed">Save Projection</button>
            </div>
          </div>
        </div>
      )}
      </>)}
    </div>
  )
}
